import React, { useState } from "react"
import { Modal } from "components/Core"
import { HiOutlineClock, HiOutlineUser, HiOutlineTag, HiHashtag, HiOutlineClipboardCopy, HiOutlineCheck } from "react-icons/hi"

interface SeriesHistoryModalProps {
  isOpen: boolean
  onClose: () => void
  serie: any
}

const SeriesHistoryModal = ({
  isOpen,
  onClose,
  serie,
}: SeriesHistoryModalProps) => {
  if (!serie) return null

  const history = serie?.omnixMetadata?.history || []
  const serieId = serie?.serieSap || serie?._omnixMetadata?.id || ""

  const CopyChip = ({ value, label }: { value: string; label: string }) => {
    const [copied, setCopied] = useState(false)
    const handleCopy = () => {
      navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    }
    return (
      <button
        onClick={handleCopy}
        title={`Copiar ${label}`}
        className="group flex items-center gap-1.5 bg-gray-100 hover:bg-indigo-50 border border-transparent hover:border-indigo-200 text-gray-800 font-bold px-2 py-0.5 rounded-md transition-all"
      >
        <span className="text-[12px] font-mono truncate max-w-[260px]">{value}</span>
        {copied
          ? <HiOutlineCheck className="text-emerald-500 shrink-0 text-[13px]" />
          : <HiOutlineClipboardCopy className="text-gray-400 group-hover:text-indigo-500 shrink-0 text-[13px] transition-colors" />
        }
      </button>
    )
  }

  return (
    <Modal mounted={isOpen} className="!w-[550px] !max-w-[95vw] shadow-2xl border border-gray-100 rounded-2xl overflow-hidden p-0">
      <div className="bg-white flex flex-col h-full max-h-[85vh]">
        {/* Header */}
        <div className="border-b border-gray-100 px-6 py-5 flex items-center justify-between sticky top-0 bg-white/95 backdrop-blur z-10">
          <div>
            <div className="flex items-center gap-2 text-blue-500 mb-2">
              <HiOutlineClock className="text-xl" />
              <h2 className="text-lg font-bold">Historial de Cambios</h2>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-gray-400 text-[11px] font-semibold uppercase tracking-wider flex items-center gap-1">
                <HiHashtag /> Serie SAP:
              </span>
              <CopyChip value={serieId} label="Serie SAP" />
            </div>
          </div>
          
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Content / Timeline */}
        <div className="overflow-y-auto w-full flex-1 p-6 relative">
          {history.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 opacity-60">
              <HiOutlineClock className="text-5xl text-gray-300 mb-2" />
              <p className="text-gray-500 font-medium">No existen registros históricos para esta serie.</p>
            </div>
          ) : (
            <div className="space-y-6 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent">
              {history.slice().reverse().map((record: any, index: number) => {
                const isLatest = index === 0;
                return (
                  <div key={index} className="relative flex items-start gap-4 w-full">
                    <div className={`flex items-center justify-center w-10 h-10 rounded-full border-4 border-white shadow-sm shrink-0 z-10 relative ${isLatest ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-500"}`}>
                      <HiOutlineClock className="text-lg" />
                    </div>
                    
                    <div className="flex-1 min-w-0 p-4 rounded-xl border border-gray-100 bg-white shadow-sm hover:shadow-md transition-shadow relative">
                      {isLatest && <div className="absolute -top-2 right-4 px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded-full uppercase">Último</div>}
                      
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-bold text-gray-800 text-sm flex items-center gap-1.5 truncate max-w-full" title={record.email}>
                          <HiOutlineUser className="text-gray-400 shrink-0" />
                          <span className="truncate">{record.email || "system@omnix.com"}</span>
                        </div>
                      </div>
                      
                      <div className="flex flex-col gap-1.5">
                        <div className="text-[11px] text-gray-500 flex items-center gap-1.5 font-mono bg-gray-50 px-2 py-1 rounded">
                          <HiOutlineTag className="text-gray-400" />
                          {record.platform || "omnix-middleware-entel"}
                        </div>
                        
                        <div className="text-[12px] font-semibold text-gray-600 mt-1 pb-1 border-b border-gray-50 flex items-center justify-between">
                          <span>Evento:</span>
                          {record.action && (
                            <span className={`px-1.5 py-[1px] text-[9px] font-black rounded uppercase tracking-wider ${
                              record.action === "CREATE" ? "bg-emerald-100 text-emerald-700" :
                              record.action === "BULK_UPDATE" ? "bg-amber-100 text-amber-700" :
                              "bg-blue-100 text-blue-700"
                            }`}>
                              {record.action}
                            </span>
                          )}
                        </div>
                        {record.modifiedFields && record.modifiedFields.length > 0 && (
                          <div className="mt-1">
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Campos modificados:</p>
                            <div className="flex flex-wrap gap-1">
                              {record.modifiedFields.map((field: string, fi: number) => (
                                <span key={fi} className="px-1.5 py-[1px] text-[10px] font-mono bg-slate-100 text-slate-600 rounded border border-slate-200">
                                  {field}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {record.transactionId && (
                          <div className="mt-1.5">
                            <CopyChip value={record.transactionId} label="Transaction ID" />
                          </div>
                        )}
                      </div>
                      
                      <div className="absolute -bottom-3 right-4 px-2 py-0.5 bg-gray-800 text-white text-[10px] font-medium rounded-full shadow-sm">
                        {(() => {
                           const dateStr = record.timestamp || record.createdAt || record.updatedAt || record.date;
                           return dateStr ? new Date(dateStr).toLocaleString() : "Sin Fecha";
                        })()}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default SeriesHistoryModal
